// 1차 QA 이후 refactored
import { useParams } from 'react-router-dom';
import { OptionDropdown } from './OptionDropdown'
import { OptionSelectedProduct } from './OptionSelectedProduct'
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserStore, useChatbotStore, useToastStore, useChatStateStore } from '../../zustand/store';
import styles from '../../style/styles/ProductOption.module.css';
import Toast from '../Toast';
import { deepSnakeToCamel, sendAddProductWithOptionsToCart } from '../../utils/utils';
import { 
    useFetchProductOptions, 
    useFetchProductOptionVariant,
    useFetchProductAllVariants,
    useUserEvent
} from '../../services/queries/chatbotQueries';
import { createOptionHandler, extractOptionType } from './cafe24Handlers/OptionHandlerFactory';
import { EnableModeEnum } from '../../type/modeState.type'
import { postCafe24ProductStockCheck } from '../../utils/apis'

const ProductOption = (props) => {
    const { itemId } = useParams();
    const { enableMode } = useChatStateStore();
    const { chatbot } = useChatbotStore();
    const { chatUserId, partnerId } = useUserStore();
    const { mutate: postUserEvent } = useUserEvent();
    // productInfo를 useMemo로 메모이제이션하여 매번 새 객체 생성 방지
    const productInfo = useMemo(() => {
        const stored = sessionStorage.getItem('product-option');
        return stored ? JSON.parse(stored) : null;
    }, []);
    
    // 상태 관리
    const [productOptions, setProductOptions] = useState([]);
    const [allVariants, setAllVariants] = useState([]);
    const [textAndFileOptions, setTextAndFileOptions] = useState([]);
    const [renderableOptions, setRenderableOptions] = useState([]);
    const [optionSelectedProductList, setOptionSelectedProductList] = useState([]);
    const [totalPrice, setTotalPrice] = useState(0);
    const [totalQuantity, setTotalQuantity] = useState(0);
    const navigate = useNavigate();
    const { setToastMessage, setIsToastVisible } = useToastStore();

    // API 호출
    const { data: optionsResponse, isLoading: isOptionsLoading } = useFetchProductOptions(itemId);
    const { mutateAsync: fetchOptionVariant } = useFetchProductOptionVariant();
    const { data: allVariantsResponse, isLoading: isAllVariantsLoading } = useFetchProductAllVariants(itemId);

    // 옵션 타입 추출 (optionsResponse가 변경될 때만)
    const optionType = useMemo(() => {
        if (!optionsResponse) return null;
        const extractedOptionType = extractOptionType(optionsResponse);
        return extractedOptionType;
    }, [optionsResponse]);

    // 옵션 타입에 따른 핸들러 생성
    // 함수 참조들은 dependency에서 제외하고, 핸들러 내부에서 직접 사용
    // optionHandler를 ref로도 저장하여 안정적인 참조 유지
    const optionHandler = useMemo(() => {
        if (!optionType || !optionsResponse) return null;
        
        return createOptionHandler(optionType, {
            itemId,
            productInfo,
            fetchOptionVariant,
            setToastMessage,
            setIsToastVisible,
            setTotalQuantity,
            setTotalPrice,
            setOptionSelectedProductList,
        });
    }, [optionType, itemId, productInfo]);
    
    // optionHandler를 ref로 저장하여 최신 참조 유지
    const optionHandlerRef = useRef(optionHandler);
    useEffect(() => {
        optionHandlerRef.current = optionHandler;
    }, [optionHandler]);

    // 옵션 데이터 초기화 완료 상태 추적
    const [isOptionsInitialized, setIsOptionsInitialized] = useState(false);

    // 옵션 데이터 초기화 및 타입별 로직 실행
    // optionsResponse, optionType, allVariantsResponse가 모두 준비될 때 실행
    useEffect(() => {
        const currentHandler = optionHandlerRef.current;
        if (!optionsResponse || !optionType || !currentHandler || !allVariantsResponse) {
            setIsOptionsInitialized(false);
            return;
        }

        const convertedResponse = deepSnakeToCamel(optionsResponse);
        const { initializedSelectOptions, initializedTextAndFileOptions } = currentHandler.initializeOptions(convertedResponse.options);
        const convertedAllVariantsResponse = deepSnakeToCamel(allVariantsResponse);
        // 타입별 초기화 로직 실행 (async 함수이므로 await 필요)
        // 하지만 useEffect 내부에서 직접 async를 사용할 수 없으므로 즉시 실행 함수 사용
        let isCancelled = false;
        (async () => {
            try {
                await currentHandler.onOptionsLoaded({
                    selectOptions: initializedSelectOptions,  // selectOptions만 productOptions로 설정
                    textAndFileOptions: initializedTextAndFileOptions,
                    allVariantsResponse: convertedAllVariantsResponse.variants,
                    setProductOptions: setProductOptions,
                    setTextAndFileOptions: setTextAndFileOptions,
                    setAllVariants: setAllVariants,
                    setOptionSelectedProductList: setOptionSelectedProductList
                });
                if (!isCancelled) {
                    setIsOptionsInitialized(true);
                }
            } catch (error) {
                if (!isCancelled) {
                    console.error('Failed to load options:', error);
                    setIsOptionsInitialized(false);
                }
            }
        })();

        // cleanup 함수: 컴포넌트가 unmount되거나 dependency가 변경되면 취소
        return () => {
            isCancelled = true;
            setIsOptionsInitialized(false);
        };
    }, [optionsResponse, optionType, allVariantsResponse]); // allVariantsResponse도 dependency에 추가

    // 옵션 선택 핸들러
    const handleOptionSelect = async (optionIndex, selectedIdx) => {
        if (!optionHandler) return;

        // 타입별 선택 로직 실행
        await optionHandler.onOptionSelect(
            {
                optionIndex,
                selectedIdx,
                productOptions,
                renderableOptions,
                textAndFileOptions,
                allVariants,
                optionSelectedProductList,
                setProductOptions,
                setRenderableOptions,
                setOptionSelectedProductList,
                fetchOptionVariant,
                initialProductOptions: optionHandler.initialProductOptions,
            }
        );
    };

    // 총 가격 및 수량 계산
    useEffect(() => {
        setTotalQuantity(optionSelectedProductList.reduce((acc, product) => acc + product.quantity, 0));
        setTotalPrice(optionSelectedProductList.reduce((acc, product) => acc + (product.price + product.additionalPrice) * product.quantity, 0));
    }, [optionSelectedProductList]);

    // 렌더링할 옵션 리스트 가져오기 (타입별로 다를 수 있음)
    // productOptions나 allVariants가 변경될 때만 업데이트 (무한 루프 방지)
    useEffect(() => {
        if (!optionHandler) {
            // optionHandler가 없으면 기본 필터링만 적용
            setRenderableOptions(productOptions.filter(option => (!option.isTextOption && !option.isFileOption)));
            return;
        }
        
        if (!productOptions || productOptions.length === 0) {
            setRenderableOptions([]);
            return;
        }
        
        if (!allVariants || allVariants.length === 0) {
            // allVariants가 없으면 기본 옵션만 표시
            setRenderableOptions(productOptions.filter(option => (!option.isTextOption && !option.isFileOption)));
            return;
        }
        
        // optionHandler를 사용하여 renderableOptions 계산
        const newRenderableOptions = optionHandler.getRenderableOptions(productOptions, allVariants);
        setRenderableOptions(newRenderableOptions);
    }, [optionHandler, productOptions, allVariants]);

    return (
        <div className={styles.ProductOption__Container}>
            <div className={styles.ProductOption__Wrapper}>
                <div className={styles.ProductOption__ContentWrapper}>
                    <div className={styles.ProductOption__Content}>
                        <div className={styles.ProductOption__Content__OptionAndProduct}>
                            <p className='h5 fc-gray-800'>
                                [옵션 선택] {productInfo?.name}
                            </p>
                            <div className='spacing-12'/>
                            <div className={styles.ProductOption__Content__OptionList}>
                                {isOptionsLoading || isAllVariantsLoading || !isOptionsInitialized || !renderableOptions ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', padding: '48px 16px'}}>
                                        <img src='/img/units/loading-spinner-28px.gif' alt='loading' width={28} height={28}/>
                                    </div>
                                ) : renderableOptions.length > 0 ? (
                                    renderableOptions.map((option, index) => (
                                        <div key={option.optionName} className={styles.ProductOption__Content__OptionItem}>
                                            <p className={`h9 fc-gray-600 ${styles.ProductOption__Content__OptionName}`}>
                                                {option.optionName} 
                                                <span className='fc-warning'>
                                                    {option.required ? '*' : ''}
                                                </span>
                                            </p>
                                            <OptionDropdown 
                                                options={option.value}
                                                isLargeType={option.displayType === 'P' || option.value.some(value => value.optionColor !== null)}
                                                selectedOption={option.selectedIndex}
                                                setSelectedOption={(idx) => handleOptionSelect(index, idx)}
                                                placeholder={true}
                                                placeholderText={`[${option.required ? '필수' : '선택'}] 옵션을 선택해주세요`}
                                                disabled={option.disabled} // 타입별로 비활성화 여부 전달
                                                setToastMessage={setToastMessage}
                                                setIsToastVisible={setIsToastVisible}
                                            />
                                        </div>
                                    ))
                                ) : renderableOptions.length === 0 ? (
                                    <div style={{ height: '50px'}} />
                                ) : null}
                            </div>
                            <div className='spacing-20'/>
                            {
                                optionSelectedProductList.map((product) => (
                                    <OptionSelectedProduct 
                                        key={product.combination}
                                        combination={product.combination}
                                        productName={product.productName}
                                        noOptionText={product?.noOptionText ?? false}
                                        optionValueList={product.optionValueList}
                                        optionCodeList={product?.optionCodeList}
                                        optionValueNoList={product?.optionValueNoList}
                                        productOptions={productOptions}
                                        existingTextOptions={product?.textOptions}
                                        quantity={product.quantity}
                                        setQuantity={(quantity) => optionHandler?.handleQuantityChange(product.combination, quantity)}
                                        price={product.price}
                                        additionalPrice={product?.additionalPrice ?? 0}
                                        processProductDeleteClicked={() => optionHandler?.handleProductDeleteClicked(product.combination)}
                                        onTextOptionChange={(textOptions) => {
                                            setOptionSelectedProductList(prev => 
                                                prev.map(p => 
                                                    p.combination === product.combination 
                                                        ? { ...p, textOptions: textOptions }
                                                        : p
                                                )
                                            );
                                        }}
                                        setToastMessage={setToastMessage}
                                        setIsToastVisible={setIsToastVisible}
                                    />
                                ))
                            }
                        </div>
                        <div className={styles.ProductOption__Content__TotalPrice}>
                            <p className='h5 fc-gray-800'>총 상품 금액</p>
                            <div style={{ display: 'flex', gap: '2px'}}>
                                <p className='h5 fc-gray-800'>{totalPrice?.toLocaleString()}원</p>
                                <p className='h6 fc-gray-500'>({totalQuantity}개)</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.ProductOption__FloatingArea}>
                    <div 
                        className={styles.ProductOption__FloatingArea__BackButton}
                        onClick={() => {
                            sessionStorage.removeItem('product-option');
                            postUserEvent({
                                eventCategory: 'chat_add_to_cart_canceled',
                                context: {
                                    productId: productInfo?.itemId,
                                },
                            });
                            navigate(`/chat/${chatbot.chatbotId}/${chatUserId}`)
                        }}
                    >
                        <p className='h5 fc-gray-600' style={{ textAlign: 'center' }}>
                            뒤로 가기
                        </p>
                    </div>
                    <div 
                        className={styles.ProductOption__FloatingArea__AddToCartButton}
                        onClick={() => {
                            if (!optionHandler) {
                                return;
                            }

                            optionHandler.onAddToCartClicked({
                                postCafe24ProductStockCheck,
                                partnerId,
                                optionSelectedProductList,
                                productOptions,
                                renderableOptions,
                                sendAddProductWithOptionsToCart,
                                chatbot,
                                chatUserId,
                                navigate
                            })
                            /* // 필수 옵션을 선택 안 했거나, 필수인 textOption 을 입력 안 했으면 경고
                            const hasRequiredOptionSelectedProduct = optionSelectedProductList.filter(product => !product.noOptionText).length > 0;
                            const hasEmptyRequiredTextOptionInput = optionSelectedProductList.filter(product => {
                                return product.textOptions.some(textOption => textOption.required && textOption.selectedValue === '');
                            }).length > 0;
                            if (productOptions.length > 0 && !hasRequiredOptionSelectedProduct) {
                                setToastMessage('필수 옵션을 선택해주세요');
                                setIsToastVisible(true);
                                return;
                            }
                            // 독립형 옵션인 경우, 모든 필수 옵션을 선택해야 함
                            if (optionType === 'independent') {
                                const requiredOptionSelectedProductCountForIndependentOption = optionSelectedProductList.filter(product => !product.noOptionText).length;
                                if (requiredOptionSelectedProductCountForIndependentOption !== renderableOptions.filter(option => option.required).length) {
                                    setToastMessage('필수 옵션을 선택해주세요');
                                    setIsToastVisible(true);
                                    return;
                                }
                            }
                            if (hasEmptyRequiredTextOptionInput) {
                                setToastMessage('필수 옵션을 입력해주세요');
                                setIsToastVisible(true);
                                return;
                            }
                            sendAddProductWithOptionsToCart(productInfo, optionSelectedProductList);
                            sessionStorage.removeItem('product-option');
                            navigate(`/chat/${chatbot.chatbotId}/${chatUserId}`) */
                        }}
                    >
                        <p className='h5 fc-white' style={{ textAlign: 'center' }}>
                            장바구니 담기
                        </p>
                    </div>
                </div>
            </div>
            <Toast bottom={enableMode === EnableModeEnum.SHRINK ? '200px' : '400px'}/>
        </div>
    );
}

export default ProductOption;

